import type { OrderEventActionFormValues } from '@klear/order-event/event-form'
import { PERMISSION_ENTITY_INVOICE_OFFER } from '@klear/constants'
import { toast } from '@klear/ui/hooks/use-toast'
import type { TRPCClientErrorLike } from '@trpc/client'
import isObject from 'lodash/isObject'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'
import { useOrderEventDomainMutations } from '@klear/ui/hooks/use-order-event-mutations'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'
import { trpc } from '@klear/ui/lib/trpc'

export function useOrderActions() {
  const { performLifecycleAction, deleteOrderEvent } = useOrderEventDomainMutations()
  const invalidateOrderEvents = useOrderEventInvalidate()
  const disburseAdvancesMutation = trpc.disbursement.disburseAdvances.useMutation()

  const handleError = (error: unknown, title: string): never => {
    console.error(title, error)
    if (error && isObject(error) && 'message' in error) {
      const trpcError = error as TRPCClientErrorLike<never>
      toast({
        title,
        description: trpcError.message || 'An error occurred',
        variant: 'destructive'
      })
    }
    throw error as Error
  }

  const runAction = async (ref: OrderEventRef, actionSchema: OrderEventActionFormValues): Promise<OrderEventRef> => {
    try {
      await performLifecycleAction(ref, actionSchema)
      return ref
    } catch (error) {
      return handleError(error, 'Error creating action event')
    }
  }

  const runDelete = async (ref: OrderEventRef): Promise<OrderEventRef> => {
    try {
      await deleteOrderEvent(ref)
      return ref
    } catch (error) {
      return handleError(error, 'Error deleting event')
    }
  }

  return {
    deleteEvent: async (_orderId: number, ref: OrderEventRef) => runDelete(ref),
    publishEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    approveEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    denyEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    acceptEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    rejectEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    fundEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) => {
      if (ref.type === PERMISSION_ENTITY_INVOICE_OFFER) {
        try {
          await disburseAdvancesMutation.mutateAsync({ disbursableIds: [ref.id] })
          await invalidateOrderEvents()
          toast({
            title: 'Advance disbursement scheduled',
            description: 'The offer will be marked funded once the payment posts.'
          })
          return ref
        } catch (error) {
          return handleError(error, 'Disbursement failed')
        }
      }
      return runAction(ref, actionSchema)
    },
    completeEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    resetEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema),
    voidEvent: async (_orderId: number, ref: OrderEventRef, actionSchema: OrderEventActionFormValues) =>
      runAction(ref, actionSchema)
  }
}
